import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import * as Icons from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';

const { Building2, ArrowUpRight, Mail } = Icons;

const applyTemplate = (str, vars) =>
  Object.entries(vars).reduce(
    (acc, [k, v]) => acc.split(`{${k}}`).join(v),
    str || ''
  );

export const MinistriesListPage = () => {
  const { ministries, settings } = useSiteData();
  const header = settings?.page_headers?.ministries || {};
  const sorted = (ministries || []).slice().sort((a, b) => a.order - b.order);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="Government Ministries"
        description="Explore the ministries and government institutions of the South East State of Somalia — their mandates, services and contact information."
        keywords="Somalia ministries, South East State ministries, Somali government departments"
      />
      <PageHeader
        title={header.title || 'Government Ministries'}
        subtitle={header.subtitle}
        crumbs={[{ label: 'Ministries' }]}
      />
      <section className="bg-[#F2EBD8] py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((m) => {
              const Icon = Icons[m.icon] || Building2;
              return (
                <Link
                  key={m.id || m.slug}
                  to={`/ministries/${m.slug}`}
                  className="group flex items-start gap-4 rounded-lg border border-[#E6DDC8] bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-[#C9A86A] hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#F6F2EA] text-[#C9A86A] transition-colors group-hover:bg-[#C9A86A] group-hover:text-white">
                    <Icon size={22} />
                  </div>
                  <div className="flex-1">
                    <div className="font-serif text-[16px] font-semibold leading-snug text-[#0B2545]">{m.name}</div>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#0B2545]/70">{m.description}</p>
                  </div>
                  <ArrowUpRight size={16} className="text-[#C9A86A] opacity-70" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export const MinistryDetailPage = () => {
  const { slug } = useParams();
  const { ministries, settings } = useSiteData();
  const list = ministries || [];
  const m = list.find((x) => x.slug === slug) || list[0];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!m) return null;
  const tmpl = settings?.ministry_template || {};
  const Icon = Icons[m.icon] || Building2;
  const vars = { name: m.name, slug: m.slug, description: m.description };
  const paragraphs = (tmpl.paragraphs || []).map((p) => applyTemplate(p, vars));
  const pillars = tmpl.pillars || [];
  return (
    <>
      <SEO
        title={m.name}
        description={m.description || `${m.name} of the South East State of Somalia — mandate, services and contact.`}
        keywords={`${m.name}, Somalia ministry, South East State, Somali government`}
      />
      <PageHeader
        title={m.name}
        subtitle={m.description}
        crumbs={[{ label: 'Ministries', to: '/ministries' }, { label: m.name }]}
      />
      <section className="bg-white py-14">
        <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#C9A86A]">
              {tmpl.eyebrow || 'About the Ministry'}
            </div>
            <h2 className="font-serif text-2xl font-bold text-[#0B2545] md:text-3xl">
              {tmpl.title || 'Mandate & Priorities'}
            </h2>
            <div className="mt-4 h-[3px] w-16 bg-[#C9A86A]" />
            <div className="mt-6 space-y-5 text-[15px] leading-relaxed text-[#0B2545]/80">
              {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {pillars.length > 0 && (
              <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {pillars.map((t, i) => (
                  <div key={i} className="rounded-lg border border-[#E6DDC8] bg-[#FAF6EC] p-5 text-center">
                    <div className="font-serif text-[15px] font-semibold text-[#0B2545]">{t}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <aside>
            <div className="rounded-lg border border-[#E6DDC8] bg-[#FAF6EC] p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#C9A86A] text-white">
                <Icon size={22} />
              </div>
              <div className="mt-4 font-serif text-[17px] font-semibold text-[#0B2545]">
                {tmpl.contact_card_title || 'Contact the Ministry'}
              </div>
              <p className="mt-2 text-[13px] text-[#0B2545]/70">
                {tmpl.contact_card_description}
              </p>
              <a href={`mailto:${m.slug}@${(settings?.site_name || 'state').toLowerCase().replace(/\s+/g, '')}.so`}
                 className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0B2545] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#08203a]">
                <Mail size={14} /> Send Email
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};
